import type { CSSObject } from '@mui/material/styles';

export interface MobileTokens {
  bg: string;
  surface: string;
  surfaceRaised: string;
  border: string;
  accent: string;
  accentDim: string;
  warning: string;
  danger: string;
  info: string;
  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
}

export const darkTokens: MobileTokens = {
  bg: '#081137',
  surface: '#162145',
  surfaceRaised: '#222E51',
  border: '#303A52',
  accent: '#00C9A5',
  accentDim: '#005545',
  warning: '#E8B54B',
  danger: '#ED756E',
  info: '#5B84C4',
  textPrimary: '#F4F2EA',
  textSecondary: '#989FAB',
  textTertiary: '#6C727E'
};

export const lightTokens: MobileTokens = {
  bg: '#F4F2EA',
  surface: '#FFFFFF',
  surfaceRaised: '#E7E3D6',
  border: '#D3CEBE',
  accent: '#00806A',
  accentDim: '#00C9A5',
  warning: '#8A6410',
  danger: '#B3423A',
  info: '#2F5590',
  textPrimary: '#081137',
  textSecondary: '#4A5162',
  textTertiary: '#6C727E'
};

export const mobileRadii = {
  tile: 12,
  row: 14,
  card: 18,
  cardLarge: 20,
  action: 16,
  pill: 26
} as const;

export const mobileFonts = {
  sans: "'Manrope', 'Helvetica Neue', Arial, sans-serif",
  serif: "'Lora', Georgia, 'Times New Roman', serif"
} as const;

/** Horizontal gutter shared by every screen, matching the mockups' 24px. */
export const mobileGutter = 24;

/** Never go lower: iOS zooms the page in when a focused input's text is under 16px, which pans the layout sideways. */
export const mobileInputFontSize = '16px';

export const pressableSx = (active: CSSObject, hover: CSSObject = active): CSSObject => ({
  '&:active': active,
  '@media (hover: hover)': {
    '&:hover': hover
  }
});

export const surfacePressSx: CSSObject = pressableSx(
  { backgroundColor: 'background.raised' },
  { backgroundColor: 'background.raised', borderColor: 'primary.dark' }
);

export const inputContainerSx = (radius: number): CSSObject => ({
  display: 'flex',
  alignItems: 'center',
  borderRadius: `${radius}px`,
  backgroundColor: 'background.paper',
  border: 1,
  borderColor: 'divider'
});

export const surfaceButtonSx: CSSObject = {
  backgroundColor: 'background.paper',
  border: 1,
  borderColor: 'divider',
  transition: 'background-color 0.15s ease, border-color 0.15s ease',
  ...surfacePressSx
};

export const searchPillSx: CSSObject = {
  ...inputContainerSx(mobileRadii.pill),
  gap: '10px',
  height: 52,
  px: '18px',
  flexShrink: 0
};

export const scrollAreaSx: CSSObject = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  WebkitOverflowScrolling: 'touch'
};

export type MobileTone = 'neutral' | 'action' | 'warning' | 'danger';

const TONE_COLOURS: Record<MobileTone, string> = {
  neutral: 'text.tertiary',
  action: 'primary.main',
  warning: 'warning.main',
  danger: 'error.main'
};

export const toneColour = (tone: MobileTone): string => TONE_COLOURS[tone];
